import { useLocation, useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import type { SoftwareAssuranceWorkflowController } from '@/hooks/workflow/useSoftwareAssuranceWorkflow'

interface IntegrationsStepSectionProps {
  workflow: SoftwareAssuranceWorkflowController
}

export function IntegrationsStepSection({ workflow }: IntegrationsStepSectionProps) {
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold">Integration Setup</h4>
          <p className="text-sm text-muted-foreground">
            Validate and link external integrations for this mission.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              navigate(
                `/metadataiq/integrations?returnTo=${encodeURIComponent(
                  location.pathname + location.search
                )}`
              )
            }
          >
            Manage Integrations
          </Button>
          <Badge variant="outline">{workflow.missionIntegrations.length} linked</Badge>
        </div>
      </div>
      {workflow.integrations.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No integrations available. Use “Manage Integrations” to add one in MetadataIQ.
        </p>
      ) : (
        <div className="grid gap-3">
          {workflow.integrations.map((integration) => {
            const binding = workflow.missionIntegrationMap.get(integration.integration_id)
            const validation = workflow.integrationValidation[integration.integration_id]
            const isLinked = !!binding
            const configDraft = workflow.integrationConfigDrafts[integration.integration_id]
            const statusMessage = workflow.integrationStatus[integration.integration_id]
            return (
              <div key={integration.integration_id} className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{integration.display_name}</p>
                    <p className="text-xs text-muted-foreground">{integration.integration_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {integration.is_demo && <Badge variant="secondary">Demo</Badge>}
                    <Badge variant={isLinked ? 'default' : 'secondary'}>
                      {isLinked ? 'Linked' : 'Not linked'}
                    </Badge>
                    {validation && (
                      <Badge variant={validation.validated ? 'default' : 'destructive'}>
                        {validation.validated ? 'Validated' : 'Validation Failed'}
                      </Badge>
                    )}
                  </div>
                </div>
                {integration.integration_type === 'emass' && (
                  <div className="grid gap-2 md:grid-cols-[1fr_auto] items-end">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">eMASS System ID</p>
                      <Input
                        placeholder="35"
                        value={configDraft?.system_id ?? ''}
                        onChange={(event) =>
                          workflow.setIntegrationConfigDrafts((prev) => ({
                            ...prev,
                            [integration.integration_id]: { system_id: event.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => workflow.handleValidateIntegration(integration.integration_id)}
                      >
                        Validate
                      </Button>
                      {isLinked ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => workflow.handleRemoveIntegration(integration.integration_id)}
                        >
                          Remove
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() =>
                            workflow.handleBindIntegration(
                              integration.integration_id,
                              integration.integration_type
                            )
                          }
                        >
                          Link
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {integration.integration_type !== 'emass' && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => workflow.handleValidateIntegration(integration.integration_id)}
                    >
                      Validate
                    </Button>
                    {isLinked ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => workflow.handleRemoveIntegration(integration.integration_id)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() =>
                          workflow.handleBindIntegration(
                            integration.integration_id,
                            integration.integration_type
                          )
                        }
                      >
                        Link
                      </Button>
                    )}
                  </div>
                )}
                {statusMessage && <p className="text-xs text-muted-foreground">{statusMessage}</p>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
